import { IconExclamationCircle, IconX } from "@tabler/icons-react";
import { type ReactNode, forwardRef, useId } from "react";
import { twMerge } from "tailwind-merge";
import { tv } from "tailwind-variants";
import { ButtonVariant } from "../Buttons/button.js";
import { IconButton } from "../Buttons/iconButton.js";
import { FieldDescription } from "../field/fieldDescription.js";
import { FieldError } from "../field/fieldError.js";
import { Size } from "../helpers/helpers.js";
import { Label } from "../Label/label.js";

interface InputPropsBase {
  /** Accessible name for inputs that do not render a visible label. */
  accessibleLabel?: string;
  /** Uses an inline wrapper when the input sits in phrasing-only content such as a heading. */
  isInline?: boolean;
  /** Applied to the root element, which holds the label, field, and description. */
  className?: string;
  label?: string;
  id?: string;
  size?: typeof Size.SMALL | typeof Size.MEDIUM;
  placeholder?: string;
  description?: string | React.ReactNode;
  errorDescription?: string;
  value: string;
  autoComplete?: string;
  autoCorrect?: string;
  autoCapitalize?: string;
  autoFocus?: boolean;
  readOnly?: boolean;
  tabIndex?: number;
  isDisabled?: boolean;
  minLength?: number;
  maxLength?: number;
  leftIcon?: ReactNode;
  /**
   * Control rendered inside the field's right edge — a reveal toggle on a
   * secret, for instance. Yields to the error icon, and takes the same slot as
   * `onClear`, so pass one or the other.
   */
  rightElement?: ReactNode;
  required?: boolean;
  onClear?: () => void;
  onKeyPress?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onPaste?: (event: React.ClipboardEvent<HTMLInputElement>) => void;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClick?: (event: React.MouseEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  inputClassName?: string;
}

export type InputProps =
  | (InputPropsBase & {
      type: "number";
      min?: number;
      max?: number;
      step?: number | string;
    })
  | (InputPropsBase & {
      type?: "text" | "password" | "date" | "datetime-local";
    });

export const inputStyle = tv({
  slots: {
    input:
      "block w-full rounded-md border-0 shadow-xs ring-1 ring-inset focus:ring-2 focus:ring-inset",
  },
  variants: {
    size: {
      [Size.SMALL]: {
        input: "h-7 px-2 py-0.5 text-xs leading-4",
      },
      [Size.MEDIUM]: {
        input: "h-8 px-2.5 py-1 text-sm leading-5",
      },
    },
    hasError: {
      true: {
        input:
          "text-red-900 placeholder-red-400 ring-red-300 focus:ring-red-500",
      },
      false: {
        input:
          "text-foreground placeholder-gray-400 ring-border-strong focus:ring-indigo-600",
      },
    },
    isDisabled: {
      true: {
        // TODO: Figure out if we need this variant, given we already use the disabled selector
        input:
          "disabled:border-border-light disabled:bg-slate-50 disabled:text-muted-foreground disabled:shadow-none",
      },
      false: {},
    },
    hasLeftIcon: {
      true: {
        input: "pl-8",
      },
      false: {},
    },
    hasRightIcon: {
      true: {
        input: "pr-8",
      },
      false: {},
    },
  },
  defaultVariants: {
    size: Size.MEDIUM,
    hasError: false,
    isDisabled: false,
    hasLeftIcon: false,
    hasRightIcon: false,
  },
});

export const Input = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const {
    id,
    label,
    placeholder,
    description,
    errorDescription,
    value,
    onChange,
    onKeyPress,
    onKeyDown,
    onClear,
    leftIcon,
    rightElement,
    inputClassName,
    onFocus,
    onBlur,
    required,
  } = props;
  const hasError = Boolean(errorDescription);
  // Fall back to a generated id so two inputs rendered without one can't end up
  // describing each other.
  const generatedId = useId();
  const descriptionId = `${id ?? generatedId}-description`;
  const descriptionText = errorDescription ?? description;

  const renderRightIcon = (): ReactNode => {
    if (hasError) {
      return (
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
          <IconExclamationCircle
            aria-hidden="true"
            className="h-5 w-5 text-red-500"
          />
        </div>
      );
    }

    if (rightElement) {
      return (
        <div className="absolute inset-y-0 right-0 flex items-center pr-0.5">
          {rightElement}
        </div>
      );
    }

    if (onClear && value) {
      return (
        <div className="absolute inset-y-0 right-0 flex items-center pr-2">
          <IconButton
            icon={<IconX className="h-4 w-4 text-gray-700" />}
            onClick={onClear}
            variant={ButtonVariant.Tertiary}
          />
        </div>
      );
    }

    return null;
  };
  const rightIcon = renderRightIcon();
  const Wrapper = props.isInline ? "span" : "div";

  const style = inputStyle({
    size: props.size,
    hasError,
    isDisabled: props.isDisabled,
    hasLeftIcon: !!leftIcon,
    hasRightIcon: !!rightIcon,
  });

  // One root element, not a Fragment, so an ancestor's `space-y-*`/`gap-*`
  // can't reach in and override the spacing between label, field, and
  // description.
  const Root = props.isInline ? "span" : "div";

  return (
    <Root
      className={twMerge(
        props.isInline ? undefined : "flex flex-col gap-2",
        props.className,
      )}
    >
      {label ? (
        <Label size={Size.SMALL}>
          {label}
          {required ? <span className="ml-1 text-red-500">*</span> : null}
        </Label>
      ) : null}
      <Wrapper
        className={twMerge(
          // An inline span would be a poor containing block for the block
          // `<input>` and the absolutely positioned icons.
          props.isInline ? "relative block" : "relative",
          hasError && "rounded-md shadow-xs",
        )}
      >
        {leftIcon ? (
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2.5">
            {leftIcon}
          </div>
        ) : null}
        <input
          aria-label={props.accessibleLabel}
          aria-describedby={descriptionText ? descriptionId : undefined}
          aria-invalid={hasError}
          autoComplete={props.autoComplete}
          autoCorrect={props.autoCorrect}
          autoCapitalize={props.autoCapitalize}
          autoFocus={props.autoFocus}
          className={style.input({ className: inputClassName })}
          disabled={props.isDisabled}
          id={id}
          max={props.type === "number" ? props.max : undefined}
          maxLength={props.maxLength ?? 2048}
          min={props.type === "number" ? props.min : undefined}
          minLength={props.minLength ?? 0}
          onBlur={onBlur}
          onChange={onChange}
          onClick={props.onClick}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          onKeyPress={onKeyPress}
          onPaste={props.onPaste}
          placeholder={placeholder}
          readOnly={props.readOnly}
          ref={ref}
          step={props.type === "number" ? props.step : undefined}
          tabIndex={props.tabIndex}
          type={props.type ?? "text"}
          value={value}
        />
        {rightIcon}
      </Wrapper>
      {errorDescription ? (
        <FieldError id={descriptionId}>{errorDescription}</FieldError>
      ) : description ? (
        <FieldDescription id={descriptionId}>{description}</FieldDescription>
      ) : null}
    </Root>
  );
});

Input.displayName = "Input";
